/**
 * 前端的金额处理。**与 `@bipay/shared/money.ts` 同一套铁律。**
 *
 * 后端把所有金额序列化成「最小单位整数的字符串」（`"1234"` = USD 12.34）。
 * 前端只有一件事不能做：**把它交给 `Number`**。
 *
 * - `Number("9007199254740993")` 会静默变成 `9007199254740992` —— 一笔对不上的账，
 *   而且没有任何报错。JPY 计价的大额交易、或者按分累计的月度汇总很容易越过 2^53。
 * - `parseFloat("12.34") * 100` 会得到 `1233.9999999999998`。
 *
 * 所以这里全程 `bigint` + 字符串切片，一次浮点运算都不做。
 * 唯一允许的 `Number` 出现在**排版**（千分位分组）里，且那时操作的是已经定型的数字串。
 *
 * 未知币种一律抛错，绝不猜 2 位小数 —— 对 JPY 猜成 2 位会让金额差 100 倍。
 */

/** ISO 4217 小数位表。与后端 `CURRENCY_EXPONENT` 逐字对齐。 */
const CURRENCY_EXPONENT: Readonly<Record<string, number>> = {
  BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0,
  PYG: 0, RWF: 0, UGX: 0, UYI: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,

  AED: 2, ALL: 2, AMD: 2, ANG: 2, AOA: 2, ARS: 2, AUD: 2, AWG: 2, AZN: 2,
  BAM: 2, BBD: 2, BDT: 2, BGN: 2, BMD: 2, BND: 2, BOB: 2, BRL: 2, BSD: 2,
  BWP: 2, BYN: 2, BZD: 2, CAD: 2, CDF: 2, CHF: 2, CNY: 2, COP: 2, CRC: 2,
  CUP: 2, CVE: 2, CZK: 2, DKK: 2, DOP: 2, DZD: 2, EGP: 2, ERN: 2, ETB: 2,
  EUR: 2, FJD: 2, FKP: 2, GBP: 2, GEL: 2, GHS: 2, GIP: 2, GMD: 2, GTQ: 2,
  GYD: 2, HKD: 2, HNL: 2, HRK: 2, HTG: 2, HUF: 2, IDR: 2, ILS: 2, INR: 2,
  JMD: 2, KES: 2, KGS: 2, KHR: 2, KYD: 2, KZT: 2, LAK: 2, LBP: 2, LKR: 2,
  LRD: 2, LSL: 2, MAD: 2, MDL: 2, MGA: 2, MKD: 2, MMK: 2, MNT: 2, MOP: 2,
  MUR: 2, MVR: 2, MWK: 2, MXN: 2, MYR: 2, MZN: 2, NAD: 2, NGN: 2, NIO: 2,
  NOK: 2, NPR: 2, NZD: 2, PAB: 2, PEN: 2, PGK: 2, PHP: 2, PKR: 2, PLN: 2,
  QAR: 2, RON: 2, RSD: 2, RUB: 2, SAR: 2, SBD: 2, SCR: 2, SEK: 2, SGD: 2,
  SHP: 2, SLE: 2, SOS: 2, SRD: 2, SSP: 2, STN: 2, SVC: 2, SZL: 2, THB: 2,
  TJS: 2, TOP: 2, TRY: 2, TTD: 2, TWD: 2, TZS: 2, UAH: 2, USD: 2, UYU: 2,
  UZS: 2, VES: 2, WST: 2, XCD: 2, YER: 2, ZAR: 2, ZMW: 2,

  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,

  CLF: 4, UYW: 4,
};

export function isKnownCurrency(currency: string): boolean {
  return Object.prototype.hasOwnProperty.call(CURRENCY_EXPONENT, currency.toUpperCase());
}

/** 已知币种列表，给下拉框用。 */
export const KNOWN_CURRENCIES: readonly string[] = Object.keys(CURRENCY_EXPONENT).sort();

export function currencyExponent(currency: string): number {
  const exp = CURRENCY_EXPONENT[currency.toUpperCase()];
  if (exp === undefined) {
    throw new Error(`未知币种 ${currency}：拒绝猜测小数位`);
  }
  return exp;
}

/**
 * 后端来的金额一律是字符串（或已经是 bigint）。转成 bigint。
 * `null` / `undefined` 原样透出，让调用方决定显示什么 —— 这里不替它兜底成 0，
 * 「没有这个字段」和「金额是 0」在账上是两件事。
 */
export function toBig(value: string | number | bigint | null | undefined): bigint | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'bigint') return value;
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value)) {
      throw new Error(`金额 ${value} 不是安全整数 —— 后端应当以字符串下发`);
    }
    return BigInt(value);
  }
  const trimmed = value.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    throw new Error(`金额 ${value} 不是最小单位整数`);
  }
  return BigInt(trimmed);
}

/**
 * 最小单位 → 人类可读的十进制串（不带币种、不带千分位）。
 * `fromMinor(1234n, 'USD')` → `'12.34'`，`fromMinor(-5n, 'JPY')` → `'-5'`。
 */
export function fromMinor(minor: bigint, currency: string): string {
  const exponent = currencyExponent(currency);
  const negative = minor < 0n;
  const magnitude = negative ? -minor : minor;
  if (exponent === 0) {
    return `${negative ? '-' : ''}${magnitude.toString()}`;
  }
  const digits = magnitude.toString().padStart(exponent + 1, '0');
  const intPart = digits.slice(0, digits.length - exponent);
  const fracPart = digits.slice(digits.length - exponent);
  return `${negative ? '-' : ''}${intPart}.${fracPart}`;
}

/** 给整数部分加千分位。操作的是已定型的数字串，不碰数值。 */
function group(intDigits: string): string {
  return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * 展示用：`"1234"` + `USD` → `'12.34'`（千分位分组，不含币种符号）。
 * 币种单独渲染成一个弱化的标签，这样同一列里数字始终右对齐可比。
 */
export function formatMinor(
  value: string | number | bigint | null | undefined,
  currency: string,
): string {
  const minor = toBig(value);
  if (minor === null) return '—';
  const plain = fromMinor(minor, currency);
  const negative = plain.startsWith('-');
  const body = negative ? plain.slice(1) : plain;
  const dot = body.indexOf('.');
  const formatted =
    dot < 0 ? group(body) : `${group(body.slice(0, dot))}${body.slice(dot)}`;
  return `${negative ? '-' : ''}${formatted}`;
}

/**
 * 人类输入的十进制串 → 最小单位 bigint。用于提现金额、费率封顶等输入框。
 * 小数位超过该币种允许的位数直接抛错，绝不四舍五入 —— 用户输入的 `1.005` USD
 * 到底是 100 分还是 101 分，只有他自己知道，替他决定就是替他丢钱。
 */
export function toMinor(input: string, currency: string): bigint {
  const exponent = currencyExponent(currency);
  const trimmed = input.trim().replace(/,/g, '');
  if (!/^-?\d*(\.\d*)?$/.test(trimmed) || trimmed === '' || trimmed === '-' || trimmed === '.') {
    throw new Error(`「${input}」不是合法金额`);
  }
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const dot = unsigned.indexOf('.');
  const intDigits = dot < 0 ? unsigned : unsigned.slice(0, dot);
  let frac = dot < 0 ? '' : unsigned.slice(dot + 1);
  while (frac.length > exponent && frac.endsWith('0')) frac = frac.slice(0, -1);
  if (frac.length > exponent) {
    throw new Error(`${currency.toUpperCase()} 最多 ${exponent} 位小数，「${input}」超了`);
  }
  const scaled = `${intDigits === '' ? '0' : intDigits}${frac.padEnd(exponent, '0')}`;
  const magnitude = BigInt(scaled);
  return negative ? -magnitude : magnitude;
}

/** 基点 → 百分数展示：`280` → `'2.80%'`。整数运算，不做浮点除法。 */
export function formatBps(bps: number): string {
  const negative = bps < 0;
  const magnitude = Math.abs(Math.trunc(bps));
  const whole = Math.floor(magnitude / 100);
  const frac = magnitude % 100;
  return `${negative ? '-' : ''}${whole}.${String(frac).padStart(2, '0')}%`;
}

/**
 * 后端下发的成功率已经是「保留 4 位小数的字符串」（见 console-api 的 `ratio()`）。
 * 这里只做展示转换，**不重新计算** —— 重算会让页面上的数和 API 返回的数出现分歧。
 */
export function formatRatio(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `${(num * 100).toFixed(2)}%`;
}
